import React, { useEffect, useRef, useState } from "react";
import { Modal, message, Input, Form, Button, Row, Col, Space } from "antd";
import "ol/ol.css";
import Map from "ol/Map";
import View from "ol/View";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import { fromLonLat, toLonLat } from "ol/proj";
import { Icon, Style } from "ol/style";
import Overlay from "ol/Overlay";
import { Translate } from "ol/interaction";
import { unByKey } from "ol/Observable";
import http from "@/utils/http";
import api from "@/utils/api";
import ipIcon from "@/assets/images/ipicon_3.png";
import "./index.css";
import { apply } from "ol-mapbox-style";
const LosAngelesMap = () => {
  const mapRef = useRef(null);
  const olMapRef = useRef(null);
  const vectorSourceRef = useRef(null);
  const vectorLayerRef = useRef(null);
  const translateInteractionRef = useRef(null);
  const tooltipRef = useRef(null);
  const overlayRef = useRef(null);
  const pointerMoveKeyRef = useRef(null);
  const markerStyleRef = useRef(null);
  const [map, setMap] = useState(null);
  const [userID, setUserID] = useState(null);
  const [form] = Form.useForm();
  const [validProperties, setValidProperties] = useState([]);
  const [isFromVendorClock, setIsFromVendorClock] = useState(false);   
  const [updating,setUpdating] = useState(null);

useEffect(() => {
  let destroyed = false;

  const initMap = async () => {
    const newMap = new Map({
      target: mapRef.current,
      view: new View({
        center: [-13150000, 4035000],
        zoom: 12,
        minZoom: 0,
        maxZoom: 20,
        projection: "EPSG:3857",
      }),
    });
    olMapRef.current = newMap;

    try {
      // 等待 style 完全加载
      await apply(
        newMap,
        "https://api.maptiler.com/maps/streets-v2/style.json?key=AQEg1jFXxCHAAxjqR7eI"
      );

      if (!destroyed) {
        initMapResources(newMap);
        setMap(newMap);
      }
    } catch (e) {
      console.error("地图样式加载失败", e);
      if (!destroyed) {
        initMapResources(newMap);
        setMap(newMap);
      }
    }
  };

  initMap();

  return () => {
    destroyed = true;
    if (pointerMoveKeyRef.current) {
      unByKey(pointerMoveKeyRef.current);
      pointerMoveKeyRef.current = null;
    }
    if (translateInteractionRef.current) {
      olMapRef.current?.removeInteraction(translateInteractionRef.current);
      translateInteractionRef.current = null;
    }
    if (overlayRef.current) {
      olMapRef.current?.removeOverlay(overlayRef.current);
      overlayRef.current = null;
    }
    tooltipRef.current?.remove();
    tooltipRef.current = null;
    olMapRef.current?.setTarget(null);
    olMapRef.current = null;
  };
}, [])
  useEffect(() => {
    if (map) {
      mapData();
    }
  }, [map]);
  useEffect(() => {
    if (validProperties.length > 0) {
      const { longitude, latitude } = validProperties[0];
      form.setFieldsValue({
        longitude: longitude,
        latitude: latitude,
      });
    }
  }, [validProperties, form]);
  const mapData = async () => {
    try {
      let params = {};
      const searchParams = new URLSearchParams(window.location.search);
      params = Object.fromEntries(searchParams.entries());
      
      if (!params.propId) {
        const hash = window.location.hash.slice(1);
        const [, queryString] = hash.split("?");
        if (queryString) {
          params = Object.fromEntries(
            new URLSearchParams(queryString).entries()
          );
        }
      }
      console.log("Received params:", params);
      const fromPage = params._from;
      if (fromPage === "vendorClock") {
        // 如果是来自 VendorClock 页面，直接使用传入的经纬度 
        setIsFromVendorClock(true);
        if (params.clockLatitude && params.clockLongitude) {
          console.log(params.clockLatitude, params.clockLongitude);
          const validProperties = [
            {
              longitude: params.clockLongitude,
              latitude: params.clockLatitude,
            },
          ];
          addMarkersVendor(validProperties, map);
          setValidProperties(validProperties);
        }
      }else{
        setIsFromVendorClock(false);
        if (params.propId) {
          setUserID(params.propId);
        }
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
    }
  };
  useEffect(() => {
    if (userID) {
      fetchDataByUserID(userID);
    }
  }, [userID]);

  const fetchDataByUserID = async (id) => {
    try {
      const response = await http.get(`${api.getPropertyId}?propId=${id}`);
      console.log("获取的数据:", response);
      const validProperties = [
        {
          longitude: parseFloat(response.data.longitude),
          latitude: parseFloat(response.data.latitude),
          propName: response.data.propName,
          propId: response.data.propId,
          prop: response.data.prop,
          groupCode: response.data.groupCode,
          street: response.data.street,
          city: response.data.city,
          state: response.data.state,
          zip: response.data.zip,
          propType: response.data.propType,
          companyName: response.data.companyName,
        },
      ];
      addMarkers(validProperties, map);
      setValidProperties(validProperties);
    } catch (error) {
      console.error("获取数据失败:", error);
    }
  };

  const defaultCenter = [-13150000, 4035000];

  const getMarkerStyle = () => {
    if (!markerStyleRef.current) {
      markerStyleRef.current = new Style({
        image: new Icon({
          anchor: [0.5, 1],
          src: ipIcon,
          scale: 0.8,
        }),
      });
    }
    return markerStyleRef.current;
  };

  function initMapResources(targetMap) {
    if (!vectorSourceRef.current) {
      vectorSourceRef.current = new VectorSource();
    }

    if (!vectorLayerRef.current) {
      vectorLayerRef.current = new VectorLayer({
        source: vectorSourceRef.current,
      });
      targetMap.addLayer(vectorLayerRef.current);
    }

    if (!tooltipRef.current) {
      const tooltip = document.createElement("div");
      tooltip.className = "map-marker-tooltip";
      tooltip.style.position = "absolute";
      tooltip.style.display = "none";
      document.body.appendChild(tooltip);
      tooltipRef.current = tooltip;
    }

    if (!overlayRef.current) {
      overlayRef.current = new Overlay({
        element: tooltipRef.current,
        offset: [10, 0],
        positioning: "bottom-left",
      });
      targetMap.addOverlay(overlayRef.current);
    }

    if (!pointerMoveKeyRef.current) {
      pointerMoveKeyRef.current = targetMap.on("pointermove", (event) => {
        const tooltip = tooltipRef.current;
        const overlay = overlayRef.current;
        if (!tooltip || !overlay) return;

        const feature = targetMap.forEachFeatureAtPixel(
          event.pixel,
          (feature) => feature
        );

        const propName = feature?.get("propName");
        if (!propName) {
          tooltip.style.display = "none";
          return;
        }

        tooltip.innerHTML = `
          Company Name: ${feature.get("companyName") || ""}<br>
          Prop: ${feature.get("prop") || ""}<br>
          Group: ${feature.get("groupCode") || ""}<br>
          Property Name: ${propName}<br>
          Street: ${feature.get("street") || ""}<br>
          City: ${feature.get("city") || ""}<br>
          State: ${feature.get("state") || ""}<br>
          Zip: ${feature.get("zip") || ""}<br>
          Prop Type: ${feature.get("propType") || ""}<br>
        `;
        tooltip.style.display = "block";
        overlay.setPosition(event.coordinate);
      });
    }

    if (!translateInteractionRef.current) {
      const translateInteraction = new Translate({
        layers: [vectorLayerRef.current],
      });

      translateInteraction.on("translateend", (event) => {
        const feature = event.features.getArray()[0];
        const newCoords = feature.getGeometry().getCoordinates();
        const newLonLat = toLonLat(newCoords);

        Modal.confirm({
          title: "Confirm update location",
          content: "Do you want to update the location of this point?",
          onOk: () => {
            updateLocation(feature.get("propId"), newLonLat[0], newLonLat[1], feature);
          },
          onCancel: () => {
            feature.getGeometry().setCoordinates(feature.get("originalCoords"));
          },
        });
      });

      targetMap.addInteraction(translateInteraction);
      translateInteractionRef.current = translateInteraction;
    }
  }

  const createMarker = (property) => {
    const longitude = parseFloat(property.longitude);
    const latitude = parseFloat(property.latitude);
    const coords = fromLonLat([longitude, latitude]);
    const marker = new Feature({
      geometry: new Point(coords),
      propName: property.propName,
      propId: property.propId,
      prop: property.prop,
      groupCode: property.groupCode,
      street: property.street,
      city: property.city,
      state: property.state,
      zip: property.zip,
      propType: property.propType,
      companyName: property.companyName,
      originalCoords: coords,
    });

    marker.setStyle(getMarkerStyle());
    return marker;
  };

  const updateMarkers = (properties, options = {}) => {
    console.log(properties);
    if (!map || !vectorSourceRef.current) return;

    const { enableTranslate = true } = options;
    translateInteractionRef.current?.setActive(enableTranslate);
    vectorSourceRef.current.clear();

    const validItems = properties.filter((property) => {
      const longitude = parseFloat(property.longitude);
      const latitude = parseFloat(property.latitude);
      return !Number.isNaN(longitude) && !Number.isNaN(latitude);
    });

    if (!validItems.length) {
      map.getView().setCenter(defaultCenter);
      map.getView().setZoom(12);
      return;
    }

    validItems.forEach((property) => {
      vectorSourceRef.current.addFeature(createMarker(property));
    });

    const firstMarkerCoords = fromLonLat([
      parseFloat(validItems[0].longitude),
      parseFloat(validItems[0].latitude),
    ]);

    map.getView().setCenter(firstMarkerCoords);
    map.getView().setZoom(16);
  };

  const addMarkers = (properties) => {
    updateMarkers(properties, { enableTranslate: true });
  };

  const addMarkersVendor = (properties) => {
    updateMarkers(properties, { enableTranslate: false });
  };

  const updateLocation = async (propId, longitude, latitude, feature) => {
    try {
      // console.log("点位信息", propId, longitude, latitude);
      const data = {
        propId: propId,
        longitude: longitude,
        latitude: latitude,
      };
      const response = await http.post(`${api.getPropertyUpdate}`, data);
      if (response.code === "1") {
        message.success("Location updated successfully!");
        const nextLongitude = response.data?.longitude ?? longitude;
        const nextLatitude = response.data?.latitude ?? latitude;
        const nextCoords = fromLonLat([nextLongitude, nextLatitude]);

        feature?.getGeometry()?.setCoordinates(nextCoords);
        feature?.set("originalCoords", nextCoords);

        form.setFieldsValue({
          longitude: nextLongitude,
          latitude: nextLatitude,
        });

        setValidProperties((prevProperties) =>
          prevProperties.map((property) =>
            property.propId === propId
              ? {
                  ...property,
                  longitude: nextLongitude,
                  latitude: nextLatitude,
                }
              : property,
          ),
        );
      }
    } catch (error) {
      console.error("Error updating location:", error);
      message.error("Failed to update the location.");
    }
  };

  // 表单提交
  const handleUpdate = async () => {
    if (updating) return;
    setUpdating(true);
    try {
      const values = await form.validateFields(); // 获取表单值
      const { longitude, latitude } = values;

      // 调用接口更新点位坐标
      const data = {
        propId: validProperties[0].propId,
        longitude: parseFloat(longitude), // 确保是数字
        latitude: parseFloat(latitude),
      };

      const response = await http.post(`${api.getPropertyUpdate}`, data);
      if (response.code === "1") {
        message.success("Location updated successfully!");

        // 获取新的坐标
        const nextLongitude = response.data?.longitude ?? data.longitude;
        const nextLatitude = response.data?.latitude ?? data.latitude;
        const newCoords = fromLonLat([
          nextLongitude,
          nextLatitude,
        ]);

        const vectorSource = vectorSourceRef.current;
        const feature = vectorSource?.getFeatures()?.[0];

        if (feature) {
          feature.getGeometry().setCoordinates(newCoords);
          feature.set("originalCoords", newCoords);
          vectorSource.changed();
        } else {
          const currentProperty = validProperties[0] || {};
          const marker = new Feature({
            geometry: new Point(newCoords),
            propName: currentProperty.propName,
            propId: currentProperty.propId || data.propId,
            prop: currentProperty.prop,
            groupCode: currentProperty.groupCode,
            street: currentProperty.street,
            city: currentProperty.city,
            state: currentProperty.state,
            zip: currentProperty.zip,
            propType: currentProperty.propType,
            companyName: currentProperty.companyName,
            originalCoords: newCoords,
          });

          marker.setStyle(getMarkerStyle());

          vectorSource?.addFeature(marker);
        }

        // 调整地图视图到新的坐标点
        map.getView().setCenter(newCoords);
        map.getView().setZoom(16);

        // 更新表单中的经纬度显示
        form.setFieldsValue({
          longitude: nextLongitude,
          latitude: nextLatitude,
        });

        // 更新 validProperties 中的经纬度
        setValidProperties((prevProperties) => {
          const updatedProperties = [...prevProperties];
          updatedProperties[0].longitude = nextLongitude;
          updatedProperties[0].latitude = nextLatitude;
          return updatedProperties;
        });
      } else {
        message.error("Failed to update location.");
      }
    } catch (error) {
      if (error?.errorFields) {
        return;
      }
      console.error("Error updating location:", error);
      message.error("Failed to update location.");
    } finally {
      setUpdating(false);
    }
  };

const getGoogleMap = () => {
  // 表单里的最新值
  const longitude = form.getFieldValue("longitude");
  const latitude = form.getFieldValue("latitude");

  if (!longitude || !latitude) {
    message.error("Coordinates not found");
    return;
  }

  // Google Maps 经纬度跳转
  const googleMapsUrl = `https://www.google.com/maps?q=${latitude},${longitude}`;

  window.open(googleMapsUrl, "_blank");
};
  return (
    <div>
      <div
        ref={mapRef}
        className="ol-map"
        style={{ width: "100%", height: "100vh" }}
      ></div>

      <div className="inputBox">
        <Form form={form} name="dynamic_rule">
          <Row gutter={[12, 8]} align="start" className="map-control-row">
          
            <Col flex="1 1 180px">
              <Form.Item
                name="latitude"
                label="Latitude"
              >
                <Input placeholder="Please input latitude" />
              </Form.Item>
            </Col>

              <Col flex="1 1 180px">
              <Form.Item
                name="longitude"
                label="Longitude"
              >
                <Input placeholder="Please input longitude" />
              </Form.Item>
            </Col>
              {!isFromVendorClock && (  
          <Col flex="0 0 auto" className="map-action-col">
            <Space>
              <Button type="primary" onClick={handleUpdate} loading={updating}>
                Update
              </Button>
              <Button type="primary" onClick={getGoogleMap}>
                Google Map
              </Button>
            </Space>
          </Col>
      )}
          </Row>
        </Form>
      </div>
    </div>
  );
};

export default LosAngelesMap;
